/**
 * @file price-update-request.tsx
 * @module app/modules/price-update-request/price-update-request
 * @description Implements the price update request workflow for price-update requests.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.1.1
 * @version 2.1.2
 */

import { useEffect, useState } from "react";
import { Button, Card, Col, Form, FormLabel, Row } from "react-bootstrap";
import CustomDatePicker from "../../custom/DateRange/DatePicker";
import { useNavigate } from "react-router-dom";
import Pagination from "../../../Global/pagination";
import APICallService from "../../../api/apiCallService";
import { priceUpdateRequest } from "../../../api/apiEndPoints";
import {
  All_Products,
  PAGE_LIMIT,
  View,
  Edit,
  PriceRequestAccepted,
  PriceRequestRejected,
  PriceRequestSubmitted,
} from "../../../utils/constants";
import Method from "../../../utils/methods";
import Loader from "../../../Global/loader";
import { useAuth } from "../auth";
import { useWarehouse } from "../../context/WarehouseContext";
import { success } from "../../../Global/toast";
import { CustomSelect } from "../../custom/Select/CustomSelect";
import { useNavigationHistory } from "../../../utils/useNavigationHistory";

const PriceUpdateRequest = () => {
  const { selectedWarehouse } = useWarehouse();
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const { navigateTo } = useNavigationHistory();
  const [requestData, setRequestData] = useState<any>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [totalRecords, setTotalRecords] = useState(0);
  const [page, setPage] = useState<any>(1);
  const [pageLimit, setPageLimit] = useState<any>(PAGE_LIMIT);
  const [startDate, setStartDate] = useState<any>(null);
  const [endDate, setEndDate] = useState<any>(null);
  const [fetchLoader, setFetchLoader] = useState<boolean>(false);
  const [search, setSearch] = useState<string>("");
  const [statusFilter, setStatusFilter] = useState<any>({
    value: "",
    label: "All Status",
  });

  const statusOptions = [
    { value: "", label: "All Status", title: "All Status" },
    { value: PriceRequestSubmitted, label: "Pending", title: "Pending" },
    { value: PriceRequestAccepted, label: "Approved", title: "Approved" },
    { value: PriceRequestRejected, label: "Rejected", title: "Rejected" },
  ];

  const fetchPriceUpdateRequests = async (
    page?: number,
    limit?: number,
    fromDate?: any,
    toDate?: any,
    searchTerm?: string,
    status?: string
  ) => {
    let params: any = {
      pageNo: page,
      limit: limit,
      needCount: true,
      sortKey: "createdAt",
      sortOrder: -1,
    };
    if (fromDate && toDate) {
      params.fromDate = Method.convertDateToFormat(fromDate, "YYYY-MM-DD");
      params.toDate = Method.convertDateToFormat(toDate, "YYYY-MM-DD");
    }
    if (searchTerm) {
      params.searchTerm = searchTerm;
    }
    if (status) {
      params.status = status;
    }
    if (selectedWarehouse?.value) {
      params.warehouse = selectedWarehouse?.value;
    }
    setLoading(true);
    const apiService = new APICallService(
      priceUpdateRequest.list,
      params,
      "",
      "",
      "",
      "",
      All_Products,
      selectedWarehouse?.value || null
    );
    const response = await apiService.callAPI();
    if (response) {
      setRequestData(response.records || []);
      setTotalRecords(response.total || 0);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (!Method.hasModulePermission(All_Products, currentUser)) {
      return window.history.back();
    }
    fetchPriceUpdateRequests(
      page,
      pageLimit,
      startDate,
      endDate,
      search,
      statusFilter.value
    );
  }, [page, pageLimit, selectedWarehouse]);

  const handleDateFilter = async (event: any) => {
    setStartDate(event[0]);
    setEndDate(event[1]);
    setFetchLoader(true);
    setPage(1);
    const startDateFormatted =
      event[0] && event[1]
        ? Method.convertDateToFormat(event[0], "YYYY-MM-DD")
        : "";
    const endDateFormatted =
      event[0] && event[1]
        ? Method.convertDateToFormat(event[1], "YYYY-MM-DD")
        : "";
        if (startDateFormatted && endDateFormatted) {
      setTotalRecords(0);
      setRequestData([]);
      await fetchPriceUpdateRequests(
        1,
        pageLimit,
        event[0],
        event[1],
        search,
        statusFilter.value
      );
    } else if (event[0] == null && event[1] == null) {
      await fetchPriceUpdateRequests(
        1,
        pageLimit,
        null,
        null,
        search,
        statusFilter.value
      );
    }
    setFetchLoader(false);
  };

  const handleSearch = (value: string) => {
    setSearch(value);
    setPage(1);
    fetchPriceUpdateRequests(
      1,
      pageLimit,
      startDate,
      endDate,
      value,
      statusFilter.value
    );
  };

  const handleStatusFilter = (selected: any) => {
    setStatusFilter(selected);
    setPage(1);
    fetchPriceUpdateRequests(
      1,
      pageLimit,
      startDate,
      endDate,
      search,
      selected.value
    );
  };

  const handleCurrentPage = async (val: number) => {
    if (val === page || val.toString() === "...") return;
    setLoading(true);
    setPage(val);
    await fetchPriceUpdateRequests(
      val,
      pageLimit,
      startDate,
      endDate,
      search,
      statusFilter.value
    );
    setLoading(false);
  };

  const handleNextPage = async (val: number) => {
    setLoading(true);
    setPage(val + 1);
    await fetchPriceUpdateRequests(
      val + 1,
      pageLimit,
      startDate,
      endDate,
      search,
      statusFilter.value
    );
    setLoading(false);
  };

  const handlePreviousPage = async (val: number) => {
    setLoading(true);
    setPage(val - 1);
    await fetchPriceUpdateRequests(
      val - 1,
      pageLimit,
      startDate,
      endDate,
      search,
      statusFilter.value
    );
    setLoading(false);
  };

  const handlePageLimit = async (event: any) => {
    setLoading(true);
    setPage(1);
    setTotalRecords(0);
    await setPageLimit(parseInt(event.target.value));
    await fetchPriceUpdateRequests(
      1,
      event.target.value,
      startDate,
      endDate,
      search,
      statusFilter.value
    );
    setLoading(false);
  };

  const handleApprove = async (id: string) => {
    if (
      !window.confirm(
        "Are you sure you want to approve this price update request?"
      )
    ) {
      return;
    }
    setLoading(true);
    const apiService = new APICallService(
      priceUpdateRequest.approve,
      {},
      { id: id },
      "",
      "",
      "",
      All_Products,
      selectedWarehouse?.value || null
    );
    const response = await apiService.callAPI();
    if (response) {
      success("Price update request approved successfully");
      await fetchPriceUpdateRequests(
        page,
        pageLimit,
        startDate,
        endDate,
        search,
        statusFilter.value
      );
    }
    setLoading(false);
  };

  const handleReject = async (id: string) => {
    if (
      !window.confirm(
        "Are you sure you want to reject this price update request?"
      )
    ) {
      return;
    }
    setLoading(true);
    const apiService = new APICallService(
      priceUpdateRequest.reject,
      {},
      { id: id },
      "",
      "",
      "",
      All_Products,
      selectedWarehouse?.value || null
    );
    const response = await apiService.callAPI();
    if (response) {
      success("Price update request rejected successfully");
      await fetchPriceUpdateRequests(
        page,
        pageLimit,
        startDate,
        endDate,
        search,
        statusFilter.value
      );
    }
    setLoading(false);
  };

  const getStatusBadge = (status: string) => {
    switch (status?.toString()) {
      case PriceRequestAccepted.toString():
        return <span className="badge badge-success fs-14">Approved</span>;
      case PriceRequestRejected.toString():
        return <span className="badge badge-danger fs-14">Rejected</span>;

      default:
        return <span className="badge badge-secondary fs-14">Pending</span>;
    }
  };

  return (
    <div className="p-5">
      <Row className="align-items-center g-md-5 g-3 mb-7">
        <Col xs>
          <h1 className="fs-22 fw-bolder">Price Update Request</h1>
        </Col>
        <Col md="auto">
          <div className="d-flex align-items-center">
            <FormLabel className="fs-16 fw-500 me-3">Filter by dates</FormLabel>
            <CustomDatePicker
              className="form-control bg-white min-h-60px fs-16 fw-bold text-dark min-w-md-288px min-w-175px"
              onChange={handleDateFilter}
              selectsRange={true}
              startDate={startDate}
              endDate={endDate}
              dateFormat="dd/MM/yyyy"
              showFullMonthYearPicker={true}
              maxDate={new Date()}
              isClearable={true}
              inputTextBG="bg-white"
            />
          </div>
        </Col>
      </Row>

      {/* Filters */}
      <Card className="border border-r10px mb-7">
        <Card.Body className="p-6">
          <Row className="g-4">
            <Col lg={4} md={6}>
              <Form.Label className="fs-16 fw-600 text-dark mb-3">
                Status
              </Form.Label>
              <CustomSelect
                value={statusFilter}
                onChange={handleStatusFilter}
                options={statusOptions}
                placeholder="Select status"
                minHeight="60px"
              />
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Table */}
      <Card className="border border-r10px">
        <Card.Body className="p-0">
          <div className="table-responsive">
            {fetchLoader ? (
              <div className="d-flex justify-content-center align-items-center min-h-400px">
                <Loader loading={fetchLoader} />
              </div>
            ) : (
              <table className="table table-rounded table-row-bordered align-middle gs-7 gy-6 mb-0">
                <thead>
                  <tr className="fs-16 fw-bold text-dark border-bottom h-70px align-middle">
                    <th className="min-w-150px">Request Date</th>
                    <th className="min-w-200px">Product</th>
                    <th className="min-w-125px">Current Price</th>
                    <th className="min-w-125px">Requested Price</th>
                    <th className="min-w-125px">Requested By Warehouse</th>
                    <th className="min-w-100px">Status</th>
                    <th className="min-w-150px text-center">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={7}>
                        <div className="d-flex justify-content-center">
                          <Loader loading={loading} />
                        </div>
                      </td>
                    </tr>
                  ) : requestData.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="text-center py-4">
                        <span className="fs-15 fw-bold text-black">
                          No data available
                        </span>
                      </td>
                    </tr>
                  ) : (
                    requestData.map((val: any, index: number) => (
                      <tr key={index}>
                        <td className="fs-15 fw-500">
                          <span className="fs-15 fw-500">
                            {Method.convertDateToDDMMYYYYHOURS(val.createdAt)}
                          </span>
                        </td>
                        <td>
                          <span className="fs-15 fw-500">
                            {val.variant?.title}
                          </span>
                        </td>
                        <td>
                          <span className="fs-15 fw-500">
                            TSh{" "}
                            {Method.getGeneralizedAmount(
                              val.oldPriceChanges[0].amount || 0
                            )}
                          </span>
                        </td>
                        <td>
                          <span className="fs-15 fw-500">
                            TSh{" "}
                            {Method.getGeneralizedAmount(
                              val.newPriceChanges[0].amount || 0
                            )}
                          </span>
                        </td>
                        <td>
                          <span className="fs-15 fw-500">
                            {val.warehouse.name || 0}
                          </span>
                        </td>
                        <td>{getStatusBadge(val.status.toString())}</td>
                        <td className="text-center">
                          <div className="d-flex gap-2 justify-content-center">
                            {Method.hasPermission(
                              All_Products,
                              View,
                              currentUser
                            ) && (
                              <Button className="uw-btn uw-btn-primary uw-btn-sm"
                                variant="primary"
                                size="sm"
                                onClick={() => {
                                  navigateTo("/price-update-request/detail", {
                                    state: { id: val._id },
                                  });
                                }}
                              >
                                View Details
                              </Button>
                            )}
                            {val.status === "pending" && (
                              <>
                                {Method.hasPermission(
                                  All_Products,
                                  Edit,
                                  currentUser
                                ) && (
                                  <>
                                    <Button className="uw-btn uw-btn-success uw-btn-sm"
                                      variant="success"
                                      size="sm"
                                      onClick={() => handleApprove(val._id)}
                                      disabled={loading}
                                    >
                                      Approve
                                    </Button>
                                    <Button className="uw-btn uw-btn-danger uw-btn-sm"
                                      variant="danger"
                                      size="sm"
                                      onClick={() => handleReject(val._id)}
                                      disabled={loading}
                                    >
                                      Reject
                                    </Button>
                                  </>
                                )}
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            )}
          </div>
        </Card.Body>
      </Card>

      {totalRecords > 0 && !fetchLoader ? (
        <Pagination
          totalRecords={totalRecords}
          currentPage={page}
          handleCurrentPage={handleCurrentPage}
          handleNextPage={handleNextPage}
          handlePreviousPage={handlePreviousPage}
          handlePageLimit={handlePageLimit}
          pageLimit={pageLimit}
        />
      ) : (
        <></>
      )}
    </div>
  );
};

export default PriceUpdateRequest;
